import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  ImageList,
  ImageListItem,
  LinearProgress,
  Checkbox,
  FormControlLabel
} from '@material-ui/core';
import {
  MuiPickersUtilsProvider,
  DatePicker,
} from '@material-ui/pickers';
import { Formik } from 'formik';
import NumberFormat from 'react-number-format';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import DateFnsUtils from '@date-io/date-fns';
import { getAvailableDeliveryMethods } from 'helpers/giftCards';
import { createGiftCardCodeApi, getGiftCardTemplatesApi, getSingleGiftCardHistoryApi, getSingleGiftCodeApi, getStoreEmailsConfigsApi } from 'api';
import * as yup from 'yup';
import { useStyles } from '../styles';


const phoneRegExp = /^(\+?\d{0,4})?\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{3}\)?)\s?-?\s?(\(?\d{4}\)?)?$/;

const CreateGiftCodeForm = ({breadcrumbPath}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const { giftcardId } = useParams<{ giftcardId?: string; }>();
  const { profile } = useSelector((state: IReduxState) => state.user);
  const {giftCardTemplates, selectedGiftCode} = useSelector((state: IReduxState) => state.gift_card);
  const { storeTimezone } = useSelector((state: IReduxState) => state.quote);
  const { storeEmailsConfigs } = useSelector((state: IReduxState) => state.store_configs);
  const templateFields = JSON.parse(selectedGiftCode?.template_fields || '{}');
  const store = profile.stores?.[0];
  const mediaStoreUrl = `${store?.apiUrl}/media/dermpro/giftcard`;
  const isEmailEnabled = storeEmailsConfigs.find((email) => email.path === 'mpgiftcard/email/enable')?.value == 1;
  const isSMSEnabled = storeEmailsConfigs.find((email) => email.path === 'mpgiftcard/sms/enable')?.value == 1;
  const giftCodeDeliveryMethods = getAvailableDeliveryMethods(isEmailEnabled, isSMSEnabled);

  useEffect(() => {
    getSingleGiftCardHistoryApi({ dispatch, giftcardId });

    if (!storeEmailsConfigs.length) {
      getStoreEmailsConfigsApi({dispatch});
    }

    if (!giftCardTemplates.length) {
      getGiftCardTemplatesApi({dispatch});
    }

    if (!selectedGiftCode && giftcardId) {
      getSingleGiftCodeApi({ dispatch, giftcardId });
    }
  }, []);

  const handleSaveGiftCode = async (values) => {
    const giftCardInfo = {
      giftcard_id: selectedGiftCode?.giftcard_id,
      store_id: selectedGiftCode?.store_id || store?.storeId,
      customer_name: values?.sender || selectedGiftCode?.customer_name,
      code: values.code,
      balance: values.balance,
      status: values.status,
      can_redeem: values.can_redeem,
      expired_at: values.expired_at,
      comment: values.comment,
      template_id: values.template_id,
      image: values.image,
      delivery_method: values.delivery_method,
      delivery_address: values.delivery_address,
      delivery_date: values.delivery_date,
      timezone: values.timezone,
      send_to_recipient: values.send_to_recipient,
      template_fields: JSON.stringify({
        recipient: values.recipient,
        sender: values.sender,
        message: values.message
      }),
      order_increment_id: selectedGiftCode?.order_increment_id
    };

    const success = await createGiftCardCodeApi({dispatch, giftCardInfo});

    if (success) {
      history.push('/gift-cards');
    }
  };

  return (
    <>
      {giftcardId && !selectedGiftCode?.giftcard_id ?
        <LinearProgress className="mt-10 mb-10" />
        :
        <Formik
          initialValues={{
            code: selectedGiftCode?.code || '',
            balance: selectedGiftCode?.balance || 0,
            status: selectedGiftCode?.status || 1,
            can_redeem: selectedGiftCode?.can_redeem ?? true,
            expired_at: selectedGiftCode?.expired_at || null,
            comment: '',
            template_id: selectedGiftCode?.template_id || '',
            image: selectedGiftCode?.image || '',
            delivery_method: String(selectedGiftCode?.delivery_method || '1'),
            delivery_address: selectedGiftCode?.delivery_address || '',
            timezone: selectedGiftCode?.timezone || storeTimezone,
            delivery_date: selectedGiftCode?.delivery_date || null,
            sender: templateFields.sender || '',
            recipient: templateFields.recipient || '',
            message: templateFields.message || '',
            send_to_recipient: selectedGiftCode?.send_to_recipient || false,
          }}
          validationSchema={yup.object({
            balance: yup
              .string()
              .required('Field is required'),
          })
            .shape({
              delivery_address: yup.string()
                .when('delivery_method', {
                  is: '1',
                  then: yup.string().email('Recipient Email must be a valid email').required('Field is required'),
                  otherwise: yup.string(),
                })
                .when('delivery_method', {
                  is: '2',
                  then: yup.string().matches(phoneRegExp, 'Phone number is not valid').required('Field is required'),
                  otherwise: yup.string(),
                })
                .when('delivery_method', {
                  is: '3',
                  then: yup.string().email('Recipient Email must be a valid email').required('Field is required'),
                  otherwise: yup.string(),
                })
            })
          }
          enableReinitialize
          onSubmit={(values) => handleSaveGiftCode(values)}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => {
            const selectedTemplate = giftCardTemplates.find((t) => t.template_id === values.template_id);
            return (
              <form noValidate autoComplete="off" className="py-4 px-2" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography color="primary" variant="h5">
                      Gift Code Information
                    </Typography>
                    <Divider />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Grid container>
                      {giftcardId &&
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Gift Code
                        </Typography>
                        <Typography className="mb-2 mt-4">
                          {selectedGiftCode.code}
                        </Typography>
                      </Grid>}
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Balance <span className="text-error">*</span>
                        </Typography>
                        <NumberFormat
                          allowNegative={false}
                          thousandSeparator={','}
                          decimalSeparator={'.'}
                          decimalScale={2}
                          fixedDecimalScale
                          className="my-2"
                          variant="outlined"
                          fullWidth
                          customInput={TextField}
                          name="balance"
                          value={values.balance}
                          onValueChange={(val) => setFieldValue('balance', val.floatValue)}
                          onBlur={handleBlur}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">$</InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Status
                        </Typography>
                        {[1, 2].includes(values.status) ?
                          <Select
                            fullWidth
                            variant="outlined"
                            name="status"
                            value={values.status}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <MenuItem value={1}>Active</MenuItem>
                            <MenuItem value={2}>Inactive</MenuItem>
                          </Select>
                          :
                          <Typography className="mb-2 mt-4">
                            {selectedGiftCode.status_label}
                          </Typography>
                        }
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Is Redeemable
                        </Typography>
                        <Select
                          fullWidth
                          variant="outlined"
                          name="can_redeem"
                          value={values.can_redeem}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <MenuItem value="true">Yes</MenuItem>
                          <MenuItem value="false">No</MenuItem>
                        </Select>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Expires At
                        </Typography>
                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                          <DatePicker
                            clearable
                            disablePast
                            autoOk
                            fullWidth
                            format="MM/dd/yyyy"
                            name="expired_at"
                            value={values.expired_at}
                            inputVariant="outlined"
                            onChange={(date) => {
                              setFieldValue('expired_at', date);
                            }}
                            onBlur={handleBlur}
                            InputProps={{
                              endAdornment: (
                                <CalendarTodayIcon className="text-muted text-16" />
                              )
                            }}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Comment
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          name="comment"
                          value={values.comment}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.comment && Boolean(errors?.comment)}
                          helperText={touched?.comment && errors?.comment}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid container>
                      <Grid item xs={12} sm={12}>
                        <Typography color="primary" variant="h5">
                          Sending Information
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Template
                        </Typography>
                        <Select
                          fullWidth
                          variant="outlined"
                          name="template_id"
                          value={values.template_id}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          {giftCardTemplates.map((el) => (
                            <MenuItem
                              key={el.template_id}
                              value={el.template_id}
                            >
                              {el.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      {selectedTemplate?.images?.length > 0 ?
                        <Grid xs={12} sm={12}>
                          <Typography className="mb-2 mt-4" variant="h6">
                            Image
                          </Typography>
                          <ImageList className={classes.templateImgList} cols={4} rowHeight={164}>
                            {selectedTemplate?.images?.map((item) => (
                              <ImageListItem
                                key={item.file}
                                className={values.image === item.file ? classes.templateImgActive : classes.templateImgInactive}
                                onClick={() => setFieldValue('image', item.file)}
                              >
                                <img
                                  src={`${mediaStoreUrl}${item.file}`}
                                  alt={item.label}
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Grid>
                        :
                        null
                      }
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Sender Name
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="sender"
                          value={values.sender}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.sender && Boolean(errors?.sender)}
                          helperText={touched?.sender && errors?.sender}
                        />
                      </Grid>
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Recipient Name
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="recipient"
                          value={values.recipient}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.recipient && Boolean(errors?.recipient)}
                          helperText={touched?.recipient && errors?.recipient}
                        />
                      </Grid>
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Message
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          name="message"
                          value={values.message}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.message && Boolean(errors?.message)}
                          helperText={touched?.message && errors?.message}
                        />
                      </Grid>
                      <Grid xs={12} sm={12}>
                        <FormControlLabel
                          control={<Checkbox color="primary" checked={values.send_to_recipient} onChange={() => setFieldValue('send_to_recipient', !values.send_to_recipient)} />}
                          label="Send to the Recipient"
                          labelPlacement="end"
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Grid container>
                      <Grid item xs={12} sm={12}>
                        <Typography color="primary" variant="h5">
                          Delivery
                        </Typography>
                        <Divider />
                      </Grid>
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Delivery Method
                        </Typography>
                        <Select
                          fullWidth
                          variant="outlined"
                          name="delivery_method"
                          value={values.delivery_method}
                          onChange={(event) => {
                            setFieldValue('delivery_address', '');
                            handleChange(event);
                          }}
                          onBlur={handleBlur}
                        >
                          {Object.keys(giftCodeDeliveryMethods).map((method) => (
                            <MenuItem
                              key={method}
                              value={method}
                            >
                              {giftCodeDeliveryMethods[method]}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                      {values.delivery_method === '1' &&
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Recipient Email <span className="text-error">*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="delivery_address"
                          value={values.delivery_address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.delivery_address && Boolean(errors?.delivery_address)}
                          helperText={touched?.delivery_address && errors?.delivery_address}
                        />
                      </Grid>
                      }
                      {values.delivery_method === '2' &&
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Phone Number <span className="text-error">*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="delivery_address"
                          value={values.delivery_address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.delivery_address && Boolean(errors?.delivery_address)}
                          helperText={touched?.delivery_address && errors?.delivery_address}
                        />
                      </Grid>
                      }
                      {values.delivery_method === '3' &&
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Customer Email <span className="text-error">*</span>
                        </Typography>
                        <TextField
                          fullWidth
                          variant="outlined"
                          name="delivery_address"
                          value={values.delivery_address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.delivery_address && Boolean(errors?.delivery_address)}
                          helperText={touched?.delivery_address && errors?.delivery_address}
                        />
                      </Grid>
                      }
                      {values.delivery_method === '4' &&
                      <Grid xs={12} sm={12}>
                        <Typography className="mb-2 mt-4" variant="h6">
                          Address
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          minRows={3}
                          variant="outlined"
                          name="delivery_address"
                          value={values.delivery_address}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched?.delivery_address && Boolean(errors?.delivery_address)}
                          helperText={touched?.delivery_address && errors?.delivery_address}
                        />
                      </Grid>
                      }
                      {/* {values.delivery_method !== '3' &&
                      <>
                        <Grid xs={12} sm={12}>
                          <Typography className="mb-2 mt-4" variant="h6">
                            Delivery Date
                          </Typography>
                          <MuiPickersUtilsProvider utils={DateFnsUtils}>
                            <DatePicker
                              clearable
                              autoOk
                              fullWidth
                              format="MM/dd/yyyy"
                              name="delivery_date"
                              value={values.delivery_date}
                              inputVariant="outlined"
                              onChange={(date) => {
                                setFieldValue('delivery_date', date);
                              }}
                              onBlur={handleBlur}
                              InputProps={{
                                endAdornment: (
                                  <CalendarTodayIcon className="text-muted text-16" />
                                )
                              }}
                            />
                          </MuiPickersUtilsProvider>
                        </Grid>
                      </>
                      } */}
                    </Grid>
                  </Grid>
                </Grid>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="mt-6"
                >
                  Save Gift Code
                </Button>
                <Button
                  variant="contained"
                  className="mt-6 ml-2"
                  onClick={() => history.push(breadcrumbPath)}
                >
                  Cancel
                </Button>
              </form>
            );
          }
          }
        </Formik>}
    </>
  );
};

export default CreateGiftCodeForm;
